<template>
    <ExerciseForm v-if="editing"
                  v-model="form.fields"
                  @cancel="handleExerciseCancel"
                  @delete="handleExerciseDelete"
                  @save="handleExerciseSave"
    />

    <div class="flex items-center justify-between mb-1">
        <Label class="!mb-0">Workout Items</Label>
        <div class="border rounded px-2 text-sm cursor-pointer" @click="$emit('toggle')" v-if="localItems.length > 1">
            {{ sorting ? 'Done Sorting' : 'Sort Exercises' }}
        </div>
    </div>

    <div class="mb-3">
        <draggable v-model="localItems"
                   @start="drag=true"
                   @end="drag=false"
                   item-key="key"
                   handle=".drag-handle"
                   chosen-class="chosen"
        >
            <template #item="{element}">
                <Row class="cursor-pointer"
                     v-if="element.type === 'exercise'"
                     :exercise="element.exercise"
                     :lastExercise="lastExerciseFor(element.exercise)"
                     @click="handleExerciseEdit(element.exercise)"
                     :sortable="sorting"
                     :bordered="true"
                />
                <RestTimerBlock
                    v-else
                    :countdown-sound-enabled="restTimerCountdownSound"
                    :duration="Number(element.restTimer.duration || restTimerDuration || 90)"
                    :label="element.restTimer.label"
                    :remaining-value="element.restTimer.remaining ?? null"
                    :removable="element.restTimer.source === 'custom'"
                    :rank="element.restTimer.rank"
                    :source="element.restTimer.source || 'custom'"
                    :sortable="sorting"
                    :status="element.restTimer.status || 'ready'"
                    :template-id="templateId"
                    :template-item-rank="element.restTimer.templateItemRank"
                    @change="handleRestTimerChange(element.key, $event)"
                    @remove="handleRestTimerRemove(element.key)"
                />
            </template>
        </draggable>
    </div>

    <div class="grid grid-cols-1 gap-3">
        <Button block primary outlined @click="handleExerciseAdd">
            Add Exercise
        </Button>
        <Button v-if="restTimerEnabled" block secondary outlined @click="handleRestTimerAdd">
            <Icon fixedWidth icon="stopwatch"/>
            Add Rest Timer
        </Button>
    </div>
</template>

<script>
import {defineComponent} from 'vue'
import {mapActions} from 'vuex';
import Row from '@/components/Exercise/Row.vue';
import RestTimerBlock from '@/components/RestTimerBlock.vue';
import ExerciseForm from '@/components/Exercise/Form.vue';
import {Exercise, Form} from '@/internal.js';
import {uuid} from '@/helpers.js';
import draggable from 'vuedraggable'
import {capturePostHogEvent} from '@/utils/posthog';

const defaultExercise = {
  advanced: false,
  bandIds: [],
  duration: 0,
  exhaustionLevel: null,
  movementId: null,
  notes: null,
  partialRepetitions: null,
  rank: null,
  repetitions: null,
  weight: null,
};

export default defineComponent({
  name: 'ExercisesInput',
  components: {
    draggable,
    ExerciseForm,
    RestTimerBlock,
    Row,
  },
  emits: [
    'update:modelValue',
    'update:restTimers',
    'toggle',
  ],
  data() {
    return {
      drag: false,
      editing: false,
      form: new Form({
        ...defaultExercise,
      }),
      lastExerciseIds: {},
      localItems: [],
      syncing: false,
    };
  },
  computed: {
    exerciseItems() {
      return this.localItems
        .filter(item => item.type === 'exercise')
        .map(item => item.exercise);
    },
    lastExercisesKey() {
      return this.exerciseItems
        .map((exercise, index) => [
          this.exerciseKey(exercise, index),
          exercise.id || '',
          exercise.movementId || exercise.movement?.id || '',
          exercise.rank || '',
          exercise.workoutId || '',
        ].join(':'))
        .join('|');
    },
    nextRank() {
      return this.localItems.length + 1;
    },
  },
  methods: {
    ...mapActions({
      exerciseLast: 'exercise/last',
    }),
    buildLocalItems(exercises, restTimers) {
      const exerciseItems = (exercises || []).map((exercise, index) => {
        const rank = Number(exercise.rank || index + 1);
        const exerciseModel = exercise instanceof Exercise ? exercise : new Exercise(exercise);
        exerciseModel.rank = rank;

        return {
          key: `exercise-${this.exerciseKey(exercise, index)}`,
          type: 'exercise',
          rank,
          exercise: exerciseModel,
        };
      });

      const restTimerItems = (restTimers || []).map((timer, index) => {
        const key = timer.key || uuid();
        const rank = Number(timer.rank || timer.templateItemRank || timer.template_item_rank || index + 1);

        return {
          key,
          type: 'rest_timer',
          rank,
          restTimer: {
            key,
            label: timer.label || 'Rest Timer',
            duration: Number(timer.duration || 90),
            rank,
            remaining: Number(timer.remaining ?? timer.duration ?? 90),
            source: timer.source || 'custom',
            status: timer.status || 'ready',
            templateItemRank: timer.templateItemRank || timer.template_item_rank || null,
          },
        };
      });

      return [
        ...exerciseItems,
        ...restTimerItems,
      ].sort((a, b) => a.rank > b.rank ? 1 : -1);
    },
    emitItems() {
      const exercises = [];
      const restTimers = [];

      this.localItems.forEach((item, index) => {
        const rank = index + 1;

        if (item.type === 'exercise') {
          exercises.push(new Exercise({
            ...item.exercise,
            rank,
          }));
          return;
        }

        restTimers.push({
          ...item.restTimer,
          key: item.key,
          rank,
        });
      });

      if (JSON.stringify(exercises) !== JSON.stringify(this.modelValue)) {
        this.$emit('update:modelValue', exercises);
      }

      if (JSON.stringify(restTimers) !== JSON.stringify(this.restTimers)) {
        this.$emit('update:restTimers', restTimers);
      }
    },
    exerciseKey(exercise, index = null) {
      return exercise.id || [
        exercise.movementId || exercise.movement?.id || 'movement',
        exercise.rank || 'rank',
        index ?? this.exerciseItems.indexOf(exercise),
      ].join('-');
    },
    handleExerciseAdd() {
      this.form.fields = {
        ...defaultExercise,
      };
      this.editing = true;
    },
    handleExerciseCancel() {
      this.form.fields = {};
      this.editing = false;
    },
    handleExerciseDelete() {
      this.localItems = [
        ...this.localItems.filter((item) => {
          if (item.type !== 'exercise') {
            return true;
          }

          if (item.exercise.id === this.form.fields.id) {
            return false;
          }

          return true;
        }),
      ];
      this.editing = false;
    },
    handleExerciseEdit(exercise) {

      if (this.sorting) {
        return;
      }

      this.form.fields = {
        ...defaultExercise,
        ...exercise,
      };
      this.editing = true;
    },
    handleExerciseSave() {
      let savedExerciseKey = null;

      if (this.form.fields.id) {
        this.localItems = [
          ...this.localItems.map((item) => {
            if (item.type === 'exercise' && item.exercise.id === this.form.fields.id) {
              savedExerciseKey = item.key;

              return {
                ...item,
                exercise: new Exercise({
                  ...this.form.fields,
                  rank: item.rank,
                }),
              };
            }

            return item;
          }),
        ];
      } else {
        const id = uuid();
        savedExerciseKey = `exercise-${id}`;

        this.localItems = [
          ...this.localItems,
          {
            key: `exercise-${id}`,
            type: 'exercise',
            rank: this.nextRank,
            exercise: new Exercise({
              id,
              rank: this.nextRank,
              ...this.form.fields,
            }),
          },
        ]
      }

      this.handleFollowingRestTimerAutoStart(savedExerciseKey);
      this.editing = false;
      this.form.fields = {};
    },
    handleRestTimerAdd() {
      const duration = Number(this.restTimerDuration || 90);
      const key = uuid();

      this.localItems = [
        ...this.localItems,
        {
          key,
          type: 'rest_timer',
          rank: this.nextRank,
          restTimer: {
            key,
            source: 'custom',
            duration,
            label: 'Rest Timer',
            rank: this.nextRank,
            remaining: duration,
            status: 'ready',
          },
        },
      ];

      capturePostHogEvent('workout_rest_timer_added', {
        duration,
        used_template: !!this.templateId,
      });
    },
    handleRestTimerChange(key, changes) {
      let shouldAutoOpenNextExercise = false;

      this.localItems = this.localItems.map((item) => {
        if (item.key !== key || item.type !== 'rest_timer') {
          return item;
        }

        shouldAutoOpenNextExercise = this.shouldAutoOpenNextExercise(item.restTimer, changes);

        return {
          ...item,
          restTimer: {
            ...item.restTimer,
            ...changes,
          },
        };
      });

      if (shouldAutoOpenNextExercise) {
        this.$nextTick(() => {
          this.handleNextExerciseOpen(key);
        });
      }
    },
    handleRestTimerRemove(key) {
      this.localItems = this.localItems.filter(item => item.key !== key);

      capturePostHogEvent('workout_rest_timer_removed', {
        used_template: !!this.templateId,
      });
    },
    handleFollowingRestTimerAutoStart(exerciseKey) {
      if (!this.restTimerAutoStartOnExerciseSave || !exerciseKey) {
        return;
      }

      const exerciseIndex = this.localItems.findIndex(item => item.key === exerciseKey);
      const restTimerIndex = exerciseIndex + 1;
      const nextItem = this.localItems[restTimerIndex];

      if (exerciseIndex < 0 || !nextItem || nextItem.type !== 'rest_timer') {
        return;
      }

      if (!this.restTimerCanAutoStart(nextItem.restTimer)) {
        return;
      }

      const duration = Number(nextItem.restTimer.duration || this.restTimerDuration || 90);

      this.localItems = this.localItems.map((item, index) => {
        if (index !== restTimerIndex) {
          return item;
        }

        return {
          ...item,
          restTimer: {
            ...item.restTimer,
            duration,
            remaining: duration,
            status: 'running',
          },
        };
      });

      capturePostHogEvent('workout_rest_timer_auto_started', {
        duration,
        used_template: !!this.templateId,
      });
    },
    handleNextExerciseOpen(restTimerKey) {
      const restTimerIndex = this.localItems.findIndex(item => item.key === restTimerKey);
      const nextItem = this.localItems[restTimerIndex + 1];

      if (restTimerIndex < 0 || !nextItem || nextItem.type !== 'exercise') {
        return;
      }

      this.handleExerciseEdit(nextItem.exercise);

      capturePostHogEvent('workout_rest_timer_next_exercise_opened', {
        used_template: !!this.templateId,
      });
    },
    restTimerCanAutoStart(restTimer) {
      return ['ready', null, undefined].includes(restTimer?.status);
    },
    shouldAutoOpenNextExercise(restTimer, changes) {
      if (!this.restTimerAutoOpenNextExerciseOnComplete || this.sorting || this.editing) {
        return false;
      }

      return restTimer?.status !== 'completed' && changes?.status === 'completed';
    },
    persistedExerciseId(exercise) {
      const id = Number(exercise.id);

      return Number.isInteger(id) && id > 0 ? id : null;
    },
    lastExerciseFor(exercise) {
      const lastExerciseId = this.lastExerciseIds[this.exerciseKey(exercise)];

      return lastExerciseId ? this.$store.getters['exercise/findBy'](lastExerciseId, 'id') : null;
    },
    loadLastExercises() {
      this.exerciseItems.forEach((exercise, index) => {
        const movementId = exercise.movementId || exercise.movement?.id;

        if (!movementId) {
          return;
        }

        const key = this.exerciseKey(exercise, index);

        this.exerciseLast({
          params: {
            exerciseId: this.persistedExerciseId(exercise),
            movementId,
            rank: exercise.rank,
            workoutId: exercise.workoutId,
          },
        }).then((ids) => {
          this.lastExerciseIds = {
            ...this.lastExerciseIds,
            [key]: ids.length ? ids[0] : null,
          };
        }).catch(() => {
          this.lastExerciseIds = {
            ...this.lastExerciseIds,
            [key]: null,
          };
        });
      });
    },
    syncLocalItems() {
      const nextItems = this.buildLocalItems(this.modelValue, this.restTimers);

      if (JSON.stringify(nextItems) === JSON.stringify(this.localItems)) {
        return;
      }

      this.syncing = true;
      this.localItems = nextItems;
      this.$nextTick(() => {
        this.syncing = false;
      });
    },
  },
  props: {
    modelValue: {
      required: true,
      type: Array,
    },
    restTimerDuration: {
      default: 90,
      required: false,
      type: Number,
    },
    restTimerCountdownSound: {
      default: true,
      required: false,
      type: Boolean,
    },
    restTimerAutoStartOnExerciseSave: {
      default: false,
      required: false,
      type: Boolean,
    },
    restTimerAutoOpenNextExerciseOnComplete: {
      default: false,
      required: false,
      type: Boolean,
    },
    restTimerEnabled: {
      default: false,
      required: false,
      type: Boolean,
    },
    restTimers: {
      default: () => [],
      required: false,
      type: Array,
    },
    sorting: {
      required: false,
      type: Boolean,
    },
    templateId: {
      default: null,
      required: false,
    }
  },
  watch: {
    localItems: {
      deep: true,
      handler() {
        if (!this.syncing) {
          this.emitItems();
        }
      },
    },
    modelValue: {
      deep: true,
      immediate: true,
      handler() {
        this.syncLocalItems();
      },
    },
    restTimers: {
      deep: true,
      immediate: true,
      handler() {
        this.syncLocalItems();
      },
    },
    lastExercisesKey: {
      immediate: true,
      handler() {
        this.loadLastExercises();
      },
    },
  },
});
</script>

<style scoped lang="scss">
.chosen {
    background: #888;
}
</style>
